import { Options, Vue } from 'vue-class-component';
import store from '@/trust-admin-app/store';
import moment from 'moment';
import { Inject } from 'vue-property-decorator';
import { EventEmitter } from 'events';
import { TimeEntryApprovalView, TimeEntryType, TimeEntryView, TrustTeamMemberView } from '@/shared/modules/typescript-trustapi-client/models';
import { AppInsights } from '@/trust-admin-app/appInsights';
import { TrustAPI } from '@/shared/modules/trust-api';
import LoadingIndicator from '@/shared/components/loading-indicator/index.vue';
import ErrorIndicator from '@/shared/components/error-indicator/index.vue';
import FormatDateTimeMixin from '@/shared/mixins/format-datetime';
import TimesheetCalculationsMixin from '@/shared/mixins/timesheet-calculations';
import { orderBy } from 'lodash';
import TimesheetHeader from '@/shared/components/timesheet-header/index.vue';
import { EventForm } from '@/trust-admin-app/components/add-event-modal/event-form';
import { createCSV, escapeString } from '@/shared/components/helpers';
import DateController from '@/shared/components/date-controller/index.vue';

@Options({
  components: {
    LoadingIndicator,
    ErrorIndicator,
    TimesheetHeader,
    DateController,
  },
  mixins: [FormatDateTimeMixin, TimesheetCalculationsMixin]
})
export default class InvestigatorTimesheets extends Vue {
  private ai = new AppInsights;

  @Inject({ from: 'apiService', default: new TrustAPI() })
  private apiService!: TrustAPI;

  @Inject({ from: 'timeEntryType', default: [] })
  private timeEntryType!: TimeEntryType[];

  private timesheetApprovalHeadings = [
    { text: 'Duration', value: 'duration', sortable: false },
    { text: 'Complete time', value: 'completeTime', sortable: false },
    { text: 'Missing time', value: 'missingTime', sortable: false },
    { text: 'Annual/Personal Leave', value: 'leave', sortable: false },
    { text: 'Public holidays', value: 'publicHolidays', sortable: false },
  ]

  private timesheetEntryHeadings = [
    { text: 'Date', value: 'date', sortable: false },
    { text: 'Name', value: 'name', sortable: false },
    { text: 'Details', value: 'details', sortable: false },
    { text: 'Firm name', value: 'firmName', sortable: false },
    { text: 'Reference', value: 'reference', sortable: false },
    { text: 'Break', value: 'break', sortable: false },
    { text: 'Work hours', value: 'workHours', sortable: false },
    { text: 'Actions', value: 'actions', sortable: false },
  ]

  private showTimesheetApproval = true;

  private loadingTimeEntries = true;
  private errorLoadingTimeEntries = true;

  private loadingTimesheetApprovals = true;
  private errorLoadingTimesheetApprovals = true;

  private currentDate = moment(new Date);
  private currentDateDisplay = moment(new Date).format('MMMM YYYY');
  private calendarView = '3'; // monthly view by default; 1: day, 2: week, 3: month
	private currentDatePeriodStart = "";
  private currentDatePeriodEnd = "";

  private timesheetApproval: TimeEntryApprovalView = {};
  private timesheetEntries: TimeEntryView[] = [];
  
  mounted(): void {
    window.scrollTo(0, 0);
    this.getTimesheetApproval();
    this.getTimeEntries();
    this.eventCtrl.on('reload-events-data', () => {
      this.getTimeEntries();
      this.getTimesheetApproval();
    });
  }

  beforeUnmount(): void {
    this.eventCtrl.removeListener('reload-events-data', () => {
      this.getTimeEntries();
      this.getTimesheetApproval();
    });
  }

  private async exportTimeSheet(): Promise<void> {
    const headers: any[] = [
      'EVENT_NAME',
      'EVENT_DETAILS',
      'EVENT_START',
      'EVENT_END',
      'LEAVE_DURATION',
      'BREAK_DURATION',
      'WORK_DURATION',
      'INVESTIGATOR_ID_REF',
      'PRACTICE_NAME'
    ];

    let headersString = '';
    let dataString = '';

    headersString = headers.map((x: any) => escapeString(x)).join(',');
    headersString += '\r\n';

    try {
      const resp: any = await this.apiService.TimeEntryApi?.apiV2TimeEntryExportTimeEntriesByTrustTeamMemberIdPost({
        trustTeamMemberId: this.selectedTrustTeamMember.id,
        startDate: new Date(this.currentDatePeriodStart),
        endDate: new Date(this.currentDatePeriodEnd)
      });

      resp.data.data.forEach((timeEntry: TimeEntryView) => {

        const practice: any = timeEntry.investigation?.latestPracticeLogData;

        if (timeEntry.eventType.id == 2) {
          dataString += [
            escapeString(timeEntry.eventType.name),
            escapeString(timeEntry.notes),
            escapeString(this.$formatDateTimeTypeA(timeEntry.startDateTime)),
            escapeString(this.$formatDateTimeTypeA(timeEntry.endDateTime)),
            escapeString(this.$calculateHours(timeEntry.leaveDuration))
          ].join(',');
        } else if (timeEntry.eventType.id == 1) {
          dataString += [
            escapeString(timeEntry.eventType.name),
            escapeString(timeEntry.notes),
            escapeString(this.$formatDateTimeTypeA(timeEntry.startDateTime)),
            escapeString(this.$formatDateTimeTypeA(timeEntry.endDateTime)),
            '',
            escapeString(this.$calculateHours(timeEntry.breakDuration)),
            escapeString(this.$calculateHours(timeEntry.workHours)),
            timeEntry.investigation?.id,
            escapeString(practice?.branchId == null ? practice?.name : practice?.name + ' - ' + practice?.tradingName)
          ].join(',');
        }

        dataString += '\r\n';
        
      });
      createCSV(
        moment(this.currentDatePeriodStart).format('MM-YYYY') + '_Diary_' + this.selectedTrustTeamMember.displayName,
        headersString + dataString
      );

    } catch(err) {
      console.log(err);
    }
  }

  private getTimeEntries(): void {
    this.apiService.TimeEntryApi?.apiV2TimeEntryGetTimeEntriesByTrustTeamMemberIdPost({
      trustTeamMemberId: this.selectedTrustTeamMember.id,
      startDate: new Date(this.currentDatePeriodStart),
      endDate: new Date(this.currentDatePeriodEnd),
      showMissingTime: true
    })
    .then(res => {
      this.loadingTimeEntries = false;
      this.errorLoadingTimeEntries = false;
      this.timesheetEntries = res.data.data;
      this.timesheetEntries =  orderBy(
        this.timesheetEntries,
        'startDateTime',
        'desc'
      );
    })
    .catch(error => {
      this.loadingTimeEntries = false
      this.errorLoadingTimeEntries = true
      this.eventCtrl.emit('show-snackbar', 'error', error);
      this.ai.trackException('Error getting investigator timesheet entries: ' + error);
    });
  }

  private getTimesheetApproval(): void {
    this.apiService.TimeEntryApi?.apiV2TimeEntryGetTimeEntryApprovalByTrustTeamMemberIdPost({
      trustTeamMemberId: this.selectedTrustTeamMember.id,
      startDate: new Date(this.currentDatePeriodStart),
      endDate: new Date(this.currentDatePeriodEnd)
    })
    .then(res => {
      this.loadingTimesheetApprovals = false;
      this.errorLoadingTimesheetApprovals = false;
      this.timesheetApproval = res.data.data;
    })
    .catch(error => {
      this.loadingTimesheetApprovals = false;
      this.errorLoadingTimesheetApprovals = true;
      this.eventCtrl.emit('show-snackbar', 'error', error);
      this.ai.trackException('Error getting investigator timesheet approvals: ' + error);
    })
  }

  private approveTimeEntries(): void {
    this.apiService.TimeEntryApi?.apiV2TimeEntryApproveTimeEntriesPost({
      trustMemberId: this.selectedTrustTeamMember.id,
      startDate: this.currentDatePeriodStart,
      endDate: this.currentDatePeriodEnd
    })
    .then(() => {
      // console.log(res.data.data);
    })
    .catch(error => {
      this.eventCtrl.emit('show-snackbar', 'error', error);
      this.ai.trackException('Error approving investigator timesheet: ' + error);
    })
  }

  private showHideTSApproval(): void {
    if (this.showTimesheetApproval) {
      this.showTimesheetApproval = false
    } else {
      this.showTimesheetApproval = true
    }
  }

  private timesheetApproved(timesheetApproval: TimeEntryApprovalView): boolean {
    if (timesheetApproval.timesheetStatus === null) {
      return true
    } else if (timesheetApproval.timesheetStatus.id === 3) {
      return true
    } else {
      return false
    }
  }

  private getTimePeriod(options: any): void {
    this.currentDatePeriodStart = options.startDatePeriod;
    this.currentDatePeriodEnd = options.endDatePeriod;
    this.getTimeEntries();
    this.getTimesheetApproval();
  }

  private addEditTimesheet(timeEntry: TimeEntryView): void {
    const eventFormData: EventForm = {
      id: timeEntry.id,
      eventTypeId: timeEntry.timeEntryType?.id ?? 1,
      leaveTypeId: timeEntry.leaveType?.id ?? null,
      startDateTime: timeEntry.startDateTime,
      endDateTime: timeEntry.endDateTime,
      notes: timeEntry.notes ?? null,
      activityType: timeEntry.timeEntryActivity ?? null,
      investigation: timeEntry.investigation ?? null,
      breakDuration: timeEntry.breakDuration ?? null,
      trustTeamMemberId: timeEntry.trustTeamMember ? timeEntry.trustTeamMember.id : this.selectedTrustTeamMember.id,
      description: timeEntry.description ?? null,
    };
    this.eventCtrl.emit('showAddEditEventModal', eventFormData);
  }

  private addEntry(): void {
    this.eventCtrl.emit('showAddEditEventModal', { eventTypeId: 1, trustTeamMemberId: this.selectedTrustTeamMember.id });
  }

  private prev(value: any): void {
    this.currentDate = value;
  }

  private next(value: any): void {
    this.currentDate = value;
  }

  private today(options: any): void {
    this.calendarView = options.calendarView;
    this.currentDate = options.currentDate;
  }

  private updateDisplay(value: any): void {
    this.currentDateDisplay = value;
  }

  get eventCtrl(): EventEmitter {
    return store.getters.getEventController;
  }

  get selectedTrustTeamMember(): TrustTeamMemberView {
    return store.getters.getSelectedTrustTeamMember;
  }

}

